// Importing necessary components and libraries from Material UI, React, Redux, and other custom components and utilities
import { Alert, Avatar, Box, Card, CardContent, Chip, Divider, Grid, List, ListItem, ListItemAvatar, ListItemText, Paper, Stack, Typography } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import MDButton from 'components/MDButton';
import React, { useMemo } from 'react';
import StaticsOverview from 'common/StaticsOverview';
import ActiveIcon from 'common/customIcons/active';
import EditIcon from 'common/customIcons/edit';
import InProgressIcon from 'common/customIcons/inprogress';
import AngleRightIcon from 'common/customIcons/angle_right';
import colors from 'assets/theme/base/colors';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { formatAddress } from 'utilities/common';
import InActiveIcon from 'common/customIcons/inactive';
import { addSpaceBeforeCapital } from 'utilities/common';
import { formatUsPhoneNumber } from 'utilities/common';
import { orderDetailStatus } from 'utilities/common';
import defaultProductImage from '../../assets/images/placeholderproduct.svg';
import CartIcon from 'common/customIcons/cart';
import StoreIcon from 'common/customIcons/store';
import InventoryIcon from 'common/customIcons/inventory';
import PaymentIcon from 'common/customIcons/payment';
import { LocalShippingOutlined, SupportAgent } from '@mui/icons-material';
import { closeCustomModalDialog, openCustomModalDialog } from '../../redux/slices/modalSlice';
import { openSnackbar } from '../../redux/slices/common';
import { useDispatch } from 'react-redux';
import { fetchSingleOrder, warrantyStatusUpdate } from '../../redux/slices/orders';
import { isEmptyObject } from 'utilities/common';
import OrderProof from './OrderProof';
import OrderLogs from './OrderLogs';
import { getData } from 'utilities/common';
import { STILO } from 'utilities/constants';
import { getPermissionsByMode } from 'utilities/common';
import { hasSectionPermission } from 'utilities/common';

/** Storefront OrderStatus / CartItem parity — truncates to 2 dp (not rounded). */
function getOrderLinePercentage(offered, sale) {
  const a = Number(offered);
  const b = Number(sale);
  if (!Number.isFinite(a) || a <= 0 || !Number.isFinite(b)) return '0';
  const pct = ((a - b) / a) * 100;
  const truncated = pct >= 0 ? Math.floor(pct * 100) / 100 : Math.ceil(pct * 100) / 100;
  return parseFloat(truncated.toFixed(2)).toString();
}

function formatMoney(value) {
  const n = parseFloat(value);
  return (Number.isFinite(n) ? n : 0).toFixed(2);
}

function hasNonEmptyFreeProduct(promo) {
  if (!promo) return false;
  const dt = String(promo.discount_type || promo.promotionType || '').toLowerCase();
  if (dt === 'free_gift') return true;
  const fp = promo.free_product;
  if (fp == null) return false;
  if (Array.isArray(fp)) return fp.length > 0;
  if (typeof fp === 'object') return Object.keys(fp).length > 0;
  return Boolean(fp);
}

function getPromotionRawList(order) {
  const pd = order?.promotionData ?? order?.cart?.promotionData;
  if (Array.isArray(pd)) return pd;
  if (pd && typeof pd === 'object') {
    if (Array.isArray(pd.enginePromotionData) && pd.enginePromotionData.length > 0) {
      return pd.enginePromotionData;
    }
    if (pd.promoName || pd.promoCode) return [pd];
  }
  return [];
}

function normalizePromotions(order) {
  const raw = getPromotionRawList(order);
  const normalized = raw.map((promo) => ({
    id: promo.id || promo.code || promo.promoCode || promo.promoName,
    code: promo.code || promo.promoCode || '',
    name: promo.promoName || promo.code || promo.promoCode || 'Promotion Applied',
    description: promo.description || '',
    discount: Number(promo.discount || promo.promoDiscount || 0),
    discountType: promo.discount_type || promo.promotionType || '',
    freeProduct: hasNonEmptyFreeProduct(promo),
    isAutoApplied: Boolean(promo.autoApply || promo.checkAutoApply),
    removable: false,
  }));
  const seen = new Set();
  const out = [];
  for (const p of normalized) {
    const key = String(p.code || p.id || p.name || '').toLowerCase().trim();
    if (key && seen.has(key)) continue;
    if (key) seen.add(key);
    out.push(p);
  }

  const fromOrder = order?.currentPromos || order?.promos || [];
  const fromCart = order?.cart?.currentPromos || order?.cart?.promos || [];
  const appliedPromoCodes = (Array.isArray(fromOrder) && fromOrder.length) ? fromOrder : fromCart;
  for (const code of appliedPromoCodes || []) {
    const c = String(code ?? '').trim();
    if (!c) continue;
    const key = c.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      id: c,
      code: c,
      name: c,
      description: '',
      discount: 0,
      discountType: '',
      freeProduct: false,
      isAutoApplied: false,
      removable: false,
    });
  }
  return out;
}

function capitalizeShippingType(s) {
  if (!s || typeof s !== 'string') return '';
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/** Full storefront line-item pricing (same field order as OrderStatus / CartItemProductCard). */
function computeStorefrontLineItem(item) {
  const isFreeGiftByFlag =
    Boolean(item?.is_free_gift || item?.s_free_gift) ||
    String(item?.discount_type || '').toLowerCase() === 'free_gift';
  const freeGiftQtyRaw =
    item?.freeGiftQty ??
    item?.freeGiftQuantity ??
    item?.free_gift_quantity ??
    item?.free_quantity ??
    item?.freeQty ??
    item?.free_qty ??
    0;
  const freeGiftQtyNum = Number(freeGiftQtyRaw);
  const freeGiftQty =
    Number.isFinite(freeGiftQtyNum) && freeGiftQtyNum > 0 ? Math.floor(freeGiftQtyNum) : 0;
  const isFreeGift = isFreeGiftByFlag || freeGiftQty > 0;

  const { quantity = 1, product = {}, discount = 0 } = item;
  const {
    productId,
    productName,
    websiteProductName,
    productImages: assets = [],
    unitPrice = 0,
    salePrice,
    isSalePrice,
    productPrice,
    brandName,
  } = product;

  const legacyProductPriceAsSale = productPrice;
  const totalPrice = quantity * unitPrice;
  const totalSalePrice = quantity * (Number(legacyProductPriceAsSale) || Number(salePrice) || 0);

  const paidQty = isFreeGift ? Math.max(0, quantity - freeGiftQty) : quantity;
  const originalPerUnit = Number(salePrice || 0) || unitPrice;
  const originalLineTotal = quantity * originalPerUnit;

  const numericDiscount = Number(discount || 0);
  const safeDiscount = Number.isFinite(numericDiscount) && numericDiscount > 0 ? numericDiscount : 0;

  const lineTotalRaw = item?.totalproductprice ?? item?.totalProductPrice ?? (quantity * unitPrice);
  const lineTotalNum = Number(lineTotalRaw);
  const lineTotalFromApi = Number.isFinite(lineTotalNum) && lineTotalNum >= 0 ? lineTotalNum : totalPrice;

  const effectiveLineTotal = (safeDiscount > 0 || isFreeGift) ? originalLineTotal : lineTotalFromApi;
  const perUnit = quantity > 0 ? effectiveLineTotal / quantity : unitPrice;

  const freeGiftValue = isFreeGift ? (originalPerUnit * freeGiftQty) : 0;
  const lineAfterDiscount = Math.max(0, effectiveLineTotal - safeDiscount - freeGiftValue);
  const paidTotalPrice = isFreeGift ? lineAfterDiscount : (paidQty * perUnit);

  const hasItemDiscount =
    !isFreeGift &&
    (safeDiscount > 0 || effectiveLineTotal < originalLineTotal);
  const discountedAfterItemDiscount = hasItemDiscount ? lineAfterDiscount : effectiveLineTotal;

  const saleUnitForLine = Number(salePrice || 0) || Number(productPrice || 0) || 0;
  const totalSalePriceForDisplay = quantity * saleUnitForLine;

  return {
    isFreeGift,
    freeGiftQty,
    quantity,
    product,
    productId,
    productName,
    websiteProductName,
    assets,
    brandName,
    unitPrice,
    salePrice,
    isSalePrice,
    productPrice,
    legacyProductPriceAsSale,
    totalPrice,
    totalSalePrice,
    totalSalePriceForDisplay,
    originalLineTotal,
    paidTotalPrice,
    discountedAfterItemDiscount,
    hasItemDiscount,
    safeDiscount,
    originalPerUnit,
    saleUnitForLine,
  };
}

function zeroFloorPrice(n) {
  const s = formatMoney(n);
  return s === '0.00' ? '0.01' : s;
}

function StorefrontLineItemPrices({ item, productPromo }) {
  const c = computeStorefrontLineItem(item);
  const promoPrice = Number(productPromo?.price || 0);
  const hasProductPromo =
    !isEmptyObject(productPromo || {}) &&
    productPromo?.productId === c.productId &&
    promoPrice > 0;

  const pctUnitSale = Number(c.salePrice || 0) || Number(c.productPrice || 0) || 0;

  if (hasProductPromo) {
    const current = c.totalSalePrice - promoPrice;
    const pctStr = getOrderLinePercentage(c.unitPrice, pctUnitSale);
    return (
      <React.Fragment>
        <Typography variant="body2" fontWeight="medium" component="span" mr={1}>
          ${formatMoney(current)}
        </Typography>
        <Typography variant="body2" fontWeight="medium" component="span" color="secondary" mr={1} sx={{ textDecoration: 'line-through' }}>
          ${formatMoney(c.totalSalePrice)}
        </Typography>
        {parseFloat(pctStr) > 0 && (
          <Typography variant="body2" fontWeight="medium" component="span" color="success.main">
            {pctStr}% Off
          </Typography>
        )}
      </React.Fragment>
    );
  }

  if (c.isFreeGift) {
    const paidStr = zeroFloorPrice(c.paidTotalPrice);
    return (
      <React.Fragment>
        <Typography variant="body2" fontWeight="medium" component="span" mr={1}>
          ${paidStr}
        </Typography>
        <Typography variant="body2" fontWeight="medium" component="span" color="secondary" mr={1} sx={{ textDecoration: 'line-through' }}>
          ${formatMoney(c.originalLineTotal)}
        </Typography>
      </React.Fragment>
    );
  }

  if (c.hasItemDiscount) {
    const paidStr = zeroFloorPrice(c.discountedAfterItemDiscount);
    return (
      <React.Fragment>
        <Typography variant="body2" fontWeight="medium" component="span" mr={1}>
          ${paidStr}
        </Typography>
        <Typography variant="body2" fontWeight="medium" component="span" color="secondary" mr={1} sx={{ textDecoration: 'line-through' }}>
          ${formatMoney(c.originalLineTotal)}
        </Typography>
      </React.Fragment>
    );
  }

  if (c.isSalePrice) {
    return (
      <React.Fragment>
        <Typography variant="body2" fontWeight="medium" component="span" mr={1}>
          ${formatMoney(c.totalSalePriceForDisplay)}
        </Typography>
        <Typography variant="body2" fontWeight="medium" component="span" color="secondary" mr={1} sx={{ textDecoration: 'line-through' }}>
          ${formatMoney(c.totalPrice)}
        </Typography>
        {parseFloat(getOrderLinePercentage(c.unitPrice, pctUnitSale)) > 0 && (
          <Typography variant="body2" fontWeight="medium" component="span" color="success.main">
            {getOrderLinePercentage(c.unitPrice, pctUnitSale)}% Off
          </Typography>
        )}
      </React.Fragment>
    );
  }

  return (
    <Typography variant="body2" fontWeight="medium" component="span" mr={1}>
      ${formatMoney(c.totalPrice)}
    </Typography>
  );
}

function StorefrontLineItemBadges({ item, productPromo }) {
  const c = computeStorefrontLineItem(item);
  const promoPrice = Number(productPromo?.price || 0);
  const hasProductPromo =
    !isEmptyObject(productPromo || {}) &&
    productPromo?.productId === c.productId &&
    promoPrice > 0;

  if (hasProductPromo) return null;
  if (c.isFreeGift) return null;
  if (c.hasItemDiscount) {
    return (
      <Box mt={1}>
        <Chip label="Promo Applied" variant="outlined" color="warning" className="promo-chip chip-square" disabled />
      </Box>
    );
  }
  return null;
}

// Main component function for OrderDetails
export default function OrderDetails() {
  // Destructuring colors from the theme
  const { grey } = colors;
  const location = useLocation()
  // Getting order details from the Redux store
  const { order = {}, isLoading = false } = useSelector(state => state.orders);
  const { items, userData, orderId, emailStatus = '', deliveryAddress = {}, trackingId = '', serviceName = '', productPromo } = order;
  // Initializing navigation and dispatch hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  let orderDetailsTabFilter = searchParams.get('orderDetailsTab')
  const adminsMode = getData('adminsMode')
  const isStilo = JSON.parse(adminsMode) === STILO
  // Handler function for updating warranty status
  /**
     * Handler function for updating the warranty status of an order.
     * 
     * @param {string} status - The new status of the warranty.
     * @param {string} orderId - The ID of the order.
     * @param {string} reason - The reason for the warranty status update.
     * @param {Array} returnItems - The list of items to be returned (default is an empty array).
     */
  const warrantyStatusHanlder = (data) => {
    // Dispatch the warranty status update action with the provided parameters
    dispatch(warrantyStatusUpdate(data))
      .unwrap()
      .then((res) => {
        // Show success message if the update is successful
        showResponseMessage(res?.message, 'success');
        // Fetch the updated order details
        dispatch(fetchSingleOrder(orderId));
        // Close the custom modal dialog
        dispatch(closeCustomModalDialog());
      })
      .catch((err) => {
        // Show error message if the update fails
        showResponseMessage(err?.message, 'error');
      });
  };

  // Handler function for opening warranty dialog
  /**
     * Handler function for opening the warranty dialog.
     * 
     * @param {string} status - The new status of the warranty.
     * @param {string} orderId - The ID of the order.
     * @param {Array} returnItems - The list of items to be returned (default is an empty array).
     */
  const warrantyDialogHandler = (status, orderId, returnItems = []) => {
    // Dispatch the action to open the custom modal dialog with the provided parameters
    dispatch(openCustomModalDialog({ modal: 'WARRANTY_REASON', modalData: { status, orderId, warrantyStatusHanlder, returnItems } }));
  };

  // Function to show response messages using snackbar
  /**
     * Function to show response messages using snackbar.
     * 
     * @param {string} message - The message to be displayed in the snackbar.
     * @param {string} errortype - The type of the message, either 'success' or 'error'.
     */
  const showResponseMessage = (message, errortype) => {
    if (errortype === 'success') {
      // Dispatch action to open a success snackbar with the provided message
      dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
    }
    if (errortype === 'error') {
      // Dispatch action to open an error snackbar with the provided message
      dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
    }
  };
  // if (orderDetailsTabFilter === 'orderProof' && emailStatus === 'awaiting_shipment') {
  //   navigate(`/orders/${orderId}?orderDetailsTab=orderProof`);
  // }



  let localUser = getData('login-user-info')
  if (localUser) {
    localUser = JSON.parse(localUser)
  }

  const adminMode = localStorage.getItem("adminsMode") ? JSON.parse(localStorage.getItem("adminsMode")) : null;
  const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);

  const canCreate = hasSectionPermission(permissionsForMode, "Orders", "Create");
  const canEdit = hasSectionPermission(permissionsForMode, "Orders", "Edit/Update");
  const canView = hasSectionPermission(permissionsForMode, "Orders", "View Only");
  const canDelete = hasSectionPermission(permissionsForMode, "Orders", "Delete");

  const normalizedPromotions = useMemo(() => normalizePromotions(order), [order]);

  return (
    orderDetailsTabFilter === 'orderProof' ? <>
      <Box p={{ xs: 2, sm: 3, lg: 4 }}>
        <OrderProof orderStatus={emailStatus} isView canCreate={canCreate} canEdit={canEdit} canDelete={canDelete} />
      </Box>
    </> :
      orderDetailsTabFilter === 'logs' ? <OrderLogs /> :
        <Box p={{ xs: 2, sm: 3, lg: 4 }}>
          {/* Navigation back to previous page */}
          <Box onClick={() => navigate(location?.state || '/orders?orderFilter=')} sx={{ cursor: 'pointer' }} display="flex" alignItems="center" color='text.main' lineHeight={1} mb={3.5}>
            <AngleRightIcon fontSize="14px" />
            <Typography variant='h6' fontWeight="semibold" ml={.75}>Orders</Typography>
          </Box>
          {/* Paper component for order overview */}
          <Paper elevation={0} style={{ borderRadius: '12px' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="start" justifyContent="space-between" bgcolor="white" p={{ xs: 1, md: 2 }}>
              {/* StaticsOverview components for displaying order details */}
              <StaticsOverview
                color="warning"
                iconShadow="iconShadowWarning"
                icon={<CartIcon />}
                title="Order ID"
                count={orderId}
              />
              {
                trackingId && <StaticsOverview
                  color="primary"
                  iconShadow="iconShadowPrimary"
                  icon={<LocalShippingOutlined />}
                  title="Tracking ID"
                  count={trackingId || '-'}
                />
              }
              {
                serviceName && <StaticsOverview
                  color="success"
                  iconShadow="iconShadowSuccess"
                  icon={<SupportAgent />}
                  title="Service"
                  count={serviceName || '-'}
                />
              }
              <StaticsOverview
                color="info"
                iconShadow="iconShadowInfo"
                icon={<InventoryIcon />}
                title="Inventory"
                count="Safe"
              />
              <StaticsOverview
                color="success"
                iconShadow="iconShadowSuccess"
                icon={<StoreIcon />}
                title="Store"
                count="Hyperwolf L.E."
              />
              <StaticsOverview
                color="primary"
                iconShadow="iconShadowInfo"
                icon={<PaymentIcon />}
                title="Payment Type"
                count={(order?.walletPointsUsed > 0 && ((order?.subTotal - (order?.walletPointsUsed || 0)) > 0 ? (order?.subTotal - (order?.walletPointsUsed || 0))?.toFixed(2) : 0) === 0) ? 'Wallet' : (order?.walletPointsUsed > 0 && (order?.subTotal - order?.walletPointsUsed) > 0) ? 'Credit/Wallet' : order?.paymentOption}
              />
              <StaticsOverview
                color="#8E0CF5"
                iconShadow="iconShadowViolet"
                icon={<InProgressIcon />}
                title="Status"
                count={orderDetailStatus?.find((status => status.key === emailStatus))?.value}
              />
            </Stack>
          </Paper>

          {/* Grid container for order details and member details */}
          <Grid container spacing={2.4} my={3}>
            <Grid item xs={12} md={7}>
              {/* Card component for order details */}
              <Card variant='detailsCard' sx={{ height: '100%', justifyContent: 'space-between' }}>
                <CardContent sx={{ p: { xs: 2, md: 3 }, pb: 0 }}>
                  {
                    order?.returnItems?.length > 0 && <>
                      {/* Section for return items */}
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3} mb={3}>
                        <Typography variant='h5'>Return Items</Typography>
                        {
                          order?.warrantyStatus === 'approved' && <Box color="#24CA49" display="flex" alignItems="center">
                            <ActiveIcon width="16px" height="16px" flex="none" />
                            <Typography variant='body2' fontWeight="medium" ml={.5} textTransform="capitalize">Approved</Typography>
                          </Box>
                        }
                        {
                          order?.warrantyStatus === 'rejected' && <Box color="#FD4438" display="flex" alignItems="center">
                            <InActiveIcon width="16px" height="16px" flex="none" />
                            <Typography variant='body2' fontWeight="medium" ml={.5} textTransform="capitalize">Decline</Typography>
                          </Box>
                        }
                        {
                          !order?.warrantyStatus && <Box display="inline-flex" gap={1} className="invite-action">
                            <MDButton onClick={() => warrantyDialogHandler(true, orderId, order?.returnItems)} variant='outlined' size='small' color='success' sx={{ fontWeight: 500 }}>Approve</MDButton>
                            <MDButton onClick={() => warrantyDialogHandler(false, orderId, order?.returnItems)} variant='outlined' size='small' color='error' sx={{ fontWeight: 500 }}>Decline</MDButton>
                          </Box>
                        }
                      </Stack>
                      {/* List of return items */}
                      <List className='order-details-listing'>

                        {
                          order?.items?.filter((orderProduct =>
                            order?.returnItems?.find((returnProduct => returnProduct?.productId === orderProduct?.product?.productId)))
                          )?.map((productItem => {
                            const product = productItem?.product || {};
                            const { productId, productImages, productName = '-', websiteProductName, category, customWeight = '', brandName } = product;
                            const displayName = websiteProductName || productName;
                            const flags = Array.isArray(productItem?.flags) ? productItem.flags : [];
                            const returnItem = order?.returnItems?.find(r => r?.productId === productId);
                            const approvedItem = order?.approvedItems?.find(a => a?.productId === productId);
                            const returnChip = returnItem ? (
                              approvedItem
                                ? <Chip label="Accepted" size="small" variant="outlined" color="success" sx={{ ml: 1, height: 22 }} />
                                : <Chip label="Rejected" size="small" variant="outlined" color="warning" sx={{ ml: 1, height: 22 }} />
                            ) : null;

                            return (
                              <ListItem key={productId} alignItems="flex-start" sx={{ width: '100%', mt: 2.5, flexDirection: 'column' }}>
                                <Box display="flex" width="100%" alignItems="flex-start">
                                  <ListItemAvatar sx={{ mr: 1.5 }} className="pro-avatar-sec">
                                    <Avatar
                                      alt="No Image Found"
                                      src={productImages?.[0] || defaultProductImage}
                                      sx={{ width: 82, height: 82, borderRadius: 4 }}
                                    />
                                  </ListItemAvatar>
                                  <ListItemText
                                    primary={
                                      <React.Fragment>
                                        <Typography variant='body2' color="secondary">{brandName}</Typography>
                                        <Box display="flex" alignItems="center" flexWrap="wrap">
                                          <Link to={`/products/details/${productId}`}>
                                            <Typography variant="body2" fontWeight="semibold">{displayName}</Typography>
                                          </Link>
                                          {returnChip}
                                        </Box>
                                        <Typography variant='body2' color="secondary">
                                          <span className='mr-3'>Category: <span style={{ color: '#000000' }}>{category?.categoryName}</span></span>
                                          <span>Wt: <span style={{ color: '#000000' }}>{customWeight}</span></span>
                                        </Typography>
                                        {flags.length > 0 && (
                                          <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.75}>
                                            {flags.map((f, idx) => (f?.value ? <Chip key={`${productId}-f-${idx}`} label={f.value} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} /> : null))}
                                          </Stack>
                                        )}
                                      </React.Fragment>
                                    }
                                    secondary={
                                      <React.Fragment>
                                        <Box component="span" display="block" mt={0.5}>
                                          <StorefrontLineItemPrices item={productItem} productPromo={productPromo} />
                                        </Box>
                                        <StorefrontLineItemBadges item={productItem} productPromo={productPromo} />
                                        {Number(productItem?.proportionalDiscount || 0) !== 0 && (
                                          <Typography variant="caption" display="block" mt={1} sx={{ color: '#7C3AED', fontWeight: 600 }}>
                                            Item Discount: ${formatMoney(productItem.proportionalDiscount)}
                                          </Typography>
                                        )}
                                      </React.Fragment>
                                    }
                                  />
                                  <Box textAlign="end" flexShrink={0} ml={1}>
                                    <Stack direction="row" alignItems="center" spacing={0.75} justifyContent="flex-end">
                                      {computeStorefrontLineItem(productItem).isFreeGift && (() => {
                                        const c = computeStorefrontLineItem(productItem);
                                        const freeLabel = c.quantity > 1 && c.freeGiftQty >= 1 ? `${c.freeGiftQty} Free` : 'Free';
                                        return <Chip label={freeLabel} size="small" variant="outlined" color="success" sx={{ height: 22 }} />;
                                      })()}
                                      <Typography variant="body2" fontWeight="medium">x{productItem?.quantity ?? 0}</Typography>
                                    </Stack>
                                    {
                                      order?.approvedItems?.length === 0 ?
                                        <>
                                          {
                                            order?.warrantyStatus === 'rejected' ? <Chip label="Rejected" variant="outlined" color='error' className='promo-chip chip-square' sx={{ mt: 1 }} /> : order?.warrantyStatus === 'approved' ? <Chip label="Returned" variant="outlined" color='success' className='promo-chip chip-square' sx={{ mt: 1 }} /> : null
                                          }
                                        </>
                                        :
                                        <>
                                          {
                                            !approvedItem ?
                                              <Chip label="Rejected" variant="outlined" color='warning' className='promo-chip chip-square' sx={{ mt: 1 }} /> : <Chip label="Accepted" variant="outlined" color='success' className='promo-chip chip-square' sx={{ mt: 1 }} />
                                          }
                                        </>
                                    }
                                  </Box>
                                </Box>
                                <Box mt={1.5} width="100%" display="flex" alignItems="center" gap={1}>
                                  <Typography variant='body2' fontWeight="semibold" mb={0}>Requested Quantity:</Typography>
                                  <Typography variant='body2' className='text-left' style={{ marginTop: '2px' }}>x{returnItem?.quantity ?? 0}</Typography>
                                </Box>
                                <Box mt={1.5} width="100%" display="flex" alignItems="center" gap={1}>
                                  <Typography variant='body2' fontWeight="semibold" mb={0}>Approved Quantity:</Typography>
                                  <Typography variant='body2' className='text-left' style={{ marginTop: '2px' }}>x{approvedItem?.requestedQty ?? 0}</Typography>
                                </Box>
                                <Box mt={1.5} width="100%" display="flex" alignItems="center" gap={1}>
                                  <Typography variant='body2' fontWeight="semibold" mb={0}>Applied Discount:</Typography>
                                  <Typography variant='body2' className='text-left' style={{ marginTop: '2px' }}>${formatMoney(approvedItem?.proportionalDiscount ?? 0)}</Typography>
                                </Box>
                                {approvedItem && (
                                  <>
                                    <Box mt={1.5} width="100%" display="flex" alignItems="center" gap={1}>
                                      <Typography variant='body2' fontWeight="semibold" mb={0}>Return to Stock:</Typography>
                                      <Typography variant='body2' className='text-left' style={{ marginTop: '2px' }}>{approvedItem.isReturned ? 'Yes' : 'No'}</Typography>
                                    </Box>
                                    <Box mt={1.5} width="100%" className="w-100">
                                      <Typography variant='body2' fontWeight="semibold" mb={0}>Return Request Reason:</Typography>
                                      <Typography variant='body2' className='text-left' style={{ marginTop: '2px' }}>{approvedItem.approvalComment}</Typography>
                                    </Box>
                                  </>
                                )}
                              </ListItem>
                            );
                          }))
                        }
                      </List>
                      {
                        (order?.warrantyStatus === 'rejected' && order?.approvalComment) && <Box mt={3} border={1} borderColor="#9A9AAF" borderRadius={4}>
                          <CardContent sx={{ px: 3, py: 2 }}>
                            <Typography variant='body2' fontWeight="semibold" mb={0}>Rejection Reason:</Typography>
                            <Typography variant='body2'>{order?.approvalComment}</Typography>
                          </CardContent>
                        </Box>
                      }
                      <Divider sx={{ my: 3 }} />
                    </>
                  }

                  {/* Section for order details */}
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3} mb={3}>
                    <Typography variant='h5'>Order Details</Typography>

                    {
                      canEdit &&
                      <>
                        {!isLoading &&
                          (emailStatus !== 'delivered' && emailStatus !== 'cancelled' && emailStatus !== 'not_returnable' && emailStatus !== 'delivery_failed' && emailStatus !== 'delivery_claimed_filed')
                          && <Link to={`/orders/${orderId}?orderDetailsTab=orderDetails`}>
                            <MDButton
                              type='button'
                              variant='outlined'
                              color='dark'
                              size="small"
                              startIcon={<EditIcon width='15px' height='15px' />}
                              sx={{ borderRadius: 1.5 }}
                              disabled={isLoading}
                              loading={isLoading}
                            >
                              Edit
                            </MDButton>
                          </Link>
                        }
                      </>
                    }

                  </Stack>

                  {(Array.isArray(order?.approvedItems) && order.approvedItems.length > 0) && (
                    <Alert severity="info" sx={{ mb: 2 }}>Items from the order below was requested for return</Alert>
                  )}
                  {(!(Array.isArray(order?.approvedItems) && order.approvedItems.length > 0) && (Array.isArray(order?.returnItems) && order.returnItems.length > 0)) && (
                    <Alert severity="info" sx={{ mb: 2 }}>Items from the order below is requested for return</Alert>
                  )}

                  {/* List of order items */}
                  <Typography variant='h5' fontWeight="semibold" mb={1}>Product</Typography>
                  <List sx={{ py: 0 }}>
                    {
                      items?.map((productItem => {
                        const product = productItem?.product || {};
                        const { productId, productImages, productName = '-', websiteProductName, category, customWeight = '', brandName } = product;
                        const displayName = websiteProductName || productName;
                        const flags = Array.isArray(productItem?.flags) ? productItem.flags : [];
                        const returnItem = order?.returnItems?.find(r => r?.productId === productId);
                        const approvedItem = order?.approvedItems?.find(a => a?.productId === productId);
                        const lineComputed = computeStorefrontLineItem(productItem);
                        const freeGiftLabel = lineComputed.isFreeGift
                          ? (lineComputed.quantity > 1 && lineComputed.freeGiftQty >= 1 ? `${lineComputed.freeGiftQty} Free` : 'Free')
                          : null;
                        const returnChip = returnItem ? (
                          approvedItem
                            ? <Chip label="Accepted" size="small" variant="outlined" color="success" sx={{ ml: 1, height: 22 }} />
                            : <Chip label="Rejected" size="small" variant="outlined" color="warning" sx={{ ml: 1, height: 22 }} />
                        ) : null;
                        const itemPropDiscount = Number(productItem?.proportionalDiscount ?? product?.proportionalDiscount ?? 0);

                        return (
                          <ListItem key={productId} alignItems="flex-start" sx={{ mt: 2.5, flexDirection: 'column', alignItems: 'stretch', px: 0 }}>
                            <Box display="flex" width="100%" alignItems="flex-start">
                              <ListItemAvatar sx={{ mr: 1.5 }} className="pro-avatar-sec">
                                <Avatar
                                  alt="No Image Found"
                                  src={productImages?.[0] || defaultProductImage}
                                  sx={{ width: 82, height: 82, borderRadius: 4 }}
                                />
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <React.Fragment>
                                    <Typography variant='body2' color="secondary">{brandName}</Typography>
                                    <Box display="flex" alignItems="center" flexWrap="wrap">
                                      <Link to={`/products/details/${productId}`}>
                                        <Typography variant="body2" fontWeight="semibold">{displayName}</Typography>
                                      </Link>
                                      {returnChip}
                                    </Box>
                                    <Typography variant='body2' color="secondary">
                                      <span className='mr-3'>Category: <span style={{ color: '#000000' }}>{category?.categoryName}</span></span>
                                      <span>Wt: <span style={{ color: '#000000' }}>{customWeight}</span></span>
                                    </Typography>
                                    {flags.length > 0 && (
                                      <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.75}>
                                        {flags.map((f, idx) => (f?.value ? <Chip key={`${productId}-mf-${idx}`} label={f.value} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} /> : null))}
                                      </Stack>
                                    )}
                                  </React.Fragment>
                                }
                                secondary={
                                  <React.Fragment>
                                    <Box component="span" display="block" mt={0.5}>
                                      <StorefrontLineItemPrices item={productItem} productPromo={productPromo} />
                                    </Box>
                                    <StorefrontLineItemBadges item={productItem} productPromo={productPromo} />
                                    {itemPropDiscount !== 0 && (
                                      <Typography variant="caption" display="block" mt={1} sx={{ color: '#7C3AED', fontWeight: 600 }}>
                                        Item Discount: ${formatMoney(itemPropDiscount)}
                                      </Typography>
                                    )}
                                  </React.Fragment>
                                }
                              />
                              <Stack direction="row" alignItems="center" spacing={0.75} flexShrink={0} sx={{ mt: 0.5, ml: 1 }}>
                                {freeGiftLabel && (
                                  <Chip label={freeGiftLabel} size="small" variant="outlined" color="success" sx={{ height: 22 }} />
                                )}
                                <Typography variant="body2" fontWeight="medium">x{productItem?.quantity ?? 0}</Typography>
                              </Stack>
                            </Box>
                            {(returnItem || approvedItem) && (
                              <Box mt={2} pl={{ xs: 0, sm: 10.5 }} width="100%" borderLeft={{ sm: '3px solid' }} borderColor="divider" sx={{ pl: { xs: 0, sm: 2 } }}>
                                <Typography variant="caption" color="text.secondary" display="block">Requested Quantity: x{returnItem?.quantity ?? 0}</Typography>
                                <Typography variant="caption" color="text.secondary" display="block">Approved Quantity: x{approvedItem?.requestedQty ?? 0}</Typography>
                                <Typography variant="caption" color="text.secondary" display="block">Applied Discount: ${formatMoney(approvedItem?.proportionalDiscount ?? 0)}</Typography>
                              </Box>
                            )}
                          </ListItem>
                        );
                      }))
                    }
                  </List>

                  {normalizedPromotions.length > 0 && (
                    <Box mt={2} mb={1}>
                      <Typography variant='body2' fontWeight="semibold" mb={1.5} color="#2E2E3A">Promotions</Typography>
                      <Stack spacing={1.25}>
                        {normalizedPromotions.map((p) => (
                          <Paper key={String(p.id || p.code || p.name)} variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                            <CheckCircle sx={{ color: '#24CA49', fontSize: 22, flexShrink: 0, mt: 0.25 }} />
                            <Typography variant="body2" fontWeight="bold">{p.name}</Typography>
                          </Paper>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  <Divider sx={{ my: 3 }} />

                  <Typography variant='body2' fontWeight='medium' mb={1} color="#2E2E3A">Order Summary</Typography>
                </CardContent>

                {/* List of order summary items */}
                <List sx={{ py: 2, background: '#F1F1F1' }}>

                  <ListItem alignItems="center" sx={{ px: 3.5, py: 1.5 }}>
                    <ListItemText primary={<Typography variant="body2" fontWeight="medium" color="secondary">Subtotal</Typography>} />
                    <Typography variant="body2" fontWeight="semibold">${formatMoney(order?.subTotal)}</Typography>
                  </ListItem>

                  {Number(order?.totalDiscount) > 0 && (
                    <ListItem alignItems="center" sx={{ px: 3.5, py: 1.5 }}>
                      <ListItemText primary={<Typography variant="body2" fontWeight="medium" color="secondary">Promotions Discount</Typography>} />
                      <Typography variant="body2" fontWeight="semibold" color="error.main">-${formatMoney(order?.totalDiscount)}</Typography>
                    </ListItem>
                  )}

                  {order?.enableDeliveryFee === true && (
                    <ListItem alignItems="center" sx={{ px: 3.5, py: 1.5 }}>
                      <ListItemText primary={<Typography variant="body2" fontWeight="medium" color="secondary">Delivery Fee</Typography>} />
                      <Typography variant="body2" fontWeight="semibold">${formatMoney(order?.deliveryFee)}</Typography>
                    </ListItem>
                  )}

                  <ListItem alignItems="center" sx={{ px: 3.5, py: 1.5 }}>
                    <ListItemText primary={<Typography variant="body2" fontWeight="medium" color="secondary">{order?.shippingType ? `Shipping (${capitalizeShippingType(order.shippingType)})` : 'Shipping'}</Typography>} />
                    {!Number(order?.shippingCharges) ? (
                      <Typography variant="body2" fontWeight="semibold" color="success.main">Free</Typography>
                    ) : (
                      <Typography variant="body2" fontWeight="semibold">${formatMoney(order?.shippingCharges)}</Typography>
                    )}
                  </ListItem>

                  {Number(order?.afterTaxDiscount) > 0 && (
                    <ListItem alignItems="center" sx={{ px: 3.5, py: 1.5 }}>
                      <ListItemText primary={<Typography variant="body2" fontWeight="medium" color="secondary">Rewards Discount</Typography>} />
                      <Typography variant="body2" fontWeight="semibold" sx={{ color: '#28B446' }}>-(${formatMoney(order?.afterTaxDiscount)})</Typography>
                    </ListItem>
                  )}

                  {Number(order?.walletPointsUsed) > 0 && (
                    <ListItem alignItems="center" sx={{ px: 3.5, py: 1.5 }}>
                      <ListItemText primary={<Typography variant="body2" fontWeight="medium" color="secondary">Wallet Points Used</Typography>} />
                      <Typography variant="body2" fontWeight="semibold">- ${formatMoney(order?.walletPointsUsed)}</Typography>
                    </ListItem>
                  )}

                  <Divider sx={{ my: 1 }} />

                  <ListItem alignItems="center" sx={{ px: 3.5, py: 1.5 }}>
                    <ListItemText primary={<Typography variant="body2" fontWeight="bold">Total amount paid</Typography>} />
                    <Typography variant="body2" fontWeight="bold">${formatMoney(order?.total)}</Typography>
                  </ListItem>
                </List>
                {order?.hasOwnProperty('walletReturned') && order.walletReturned !== 0 && (
                  <div className="save-amount">
                    <p style={{ color: '#9A9AAF' }}>Amount Refunded: ${formatMoney(order.walletReturned)}</p>
                  </div>
                )}
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  {/* Section for delivery address */}
                  <Typography variant='body2' fontWeight="semibold" mb={1}>Delivery Address</Typography>
                  <Typography variant='body2' fontWeight="regular" lineHeight="1.85">
                    {
                      deliveryAddress?.apartment && <>
                        <b style={{ fontWeight: 600 }}>Apartment Number: </b>{deliveryAddress.apartment || '-'} <br />
                      </>
                    }
                    <b style={{ fontWeight: 600 }}>Address: </b>{deliveryAddress?.address || '-'} <br />
                    <b style={{ fontWeight: 600 }}>City: </b>{deliveryAddress.city || '-'} <br />
                    <b style={{ fontWeight: 600 }}>State: </b>{deliveryAddress.state || '-'} <br />
                    <b style={{ fontWeight: 600 }}>Zip Code: </b>{deliveryAddress.zipcode || deliveryAddress.zipCode || '-'}<br />
                  </Typography>
                </CardContent>
                {
                  order?.memo && <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    {/* Section for additional instructions */}
                    <Typography variant='body2' fontWeight="semibold" mb={1}>Instructions</Typography>
                    <Typography variant='body2'>{order?.memo}</Typography>
                  </CardContent>
                }
              </Card>
            </Grid>

            <Grid item xs={12} md={5}>
              {/* Card component for member details */}
              <Card variant='detailsCard' sx={{ height: '100%', justifyContent: 'space-between' }}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography variant='h5' mb={2}>Member Details</Typography>
                    {
                      (canEdit || canCreate) &&
                      <div className="d-flex align-items-start justify-content-start flex-column gap-2 client-list-rep">
                        <MDButton onClick={() => dispatch(openCustomModalDialog({ modal: 'HOT_NOTES', modalData: { memberPayload: userData, memberId: userData?.memberId, allowAdd: false } }))} size="medium" variant="outlined" color='success' className="font-weight-600 border-radius-10 fs-small">
                          Hot Notes
                        </MDButton>
                      </div>
                    }
                  </Stack>
                  {/* Grid container for member details */}
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant='caption' color={grey[600]}>Name</Typography>
                      <Link to={`/members/detail/${userData?.memberId}`}>
                        <Typography variant='body2' fontWeight="medium" lineHeight="1.25">{userData?.fullName}</Typography>
                      </Link>
                      {/* {
                      userData?.fullName && <div className="ml-2 d-flex align-items-center justify-content-start gap-1 cus-badge">
                       {userData?.isBirthdayToday && <MemberBirthday />} {userData?.newMember && <NewMember />} {userData?.vipMember && <VipMember />}
                      </div>
                    } */}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant='caption' color={grey[600]}>Member Address</Typography>
                      {isStilo ?
                        <>
                          {
                            userData?.idAddress?.address ? <Typography variant='body2' fontWeight="medium" lineHeight="1.25">{formatAddress({ ...userData?.idAddress, zipcode: userData?.idAddress?.zipcode })}</Typography> : <Typography className="fs-medium" fontWeight="medium" lineHeight="1.25">-</Typography>
                          }
                        </>
                        :
                        <>
                          {
                            userData?.deliveryAddress ? <Typography variant='body2' fontWeight="medium" lineHeight="1.25">{formatAddress({ ...userData?.deliveryAddress, zipcode: userData?.deliveryAddress?.zipcode })}</Typography> : <Typography variant='h6' fontWeight="medium" lineHeight="1.25">-</Typography>
                          }
                        </>

                      }

                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant='caption' color={grey[600]}>Phone</Typography>
                      <Typography variant='body2' fontWeight="medium" lineHeight="1.25">+1 {userData?.phone ? formatUsPhoneNumber(userData?.phone) : '-'}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant='caption' color={grey[600]}>Email</Typography>
                      <Typography variant='body2' fontWeight="medium" lineHeight="1.25">{userData?.email}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant='caption' color={grey[600]}>Member Type</Typography>
                      <Typography variant='body2' fontWeight="medium" lineHeight="1.25">{userData?.memberType ? addSpaceBeforeCapital(userData?.memberType) : '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant='caption' color={grey[600]}>Member Group</Typography>
                      <Typography variant='body2' fontWeight="medium" lineHeight="1.25">{userData?.memberShipGroup || '-'}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
  );
}